import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeuringStatusControl } from '../components/KeuringStatusControl';
import { InstallationExtras } from '../components/InstallationExtras';
import { 
  Zap, Calendar as CalendarIcon, User, Building2, Plus, Trash2, AlertTriangle, MessageSquare, Clock, 
  ExternalLink, MoreHorizontal, Mail, Edit, Phone, Sun, Battery, Car,
  CheckCircle2, XCircle, MapPin, Sparkles, Users, Wrench, Camera, Upload, Image, X, FileText, Search
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SearchSelect from '../components/SearchSelect';
import CcSelector from '../components/CcSelector';
import { propertyAPI, customerAPI, appointmentAPI, usersAPI } from '../lib/api';
import { formatDate, getGoogleMapsUrl, getWazeUrl, formatPhoneNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import DocumentLightbox from '../components/DocumentLightbox';
import OfferteDocumenten from '../components/OfferteDocumenten';

const INSTALLATION_TYPES = [
  { value: 'zonnepanelen', label: 'Zonnepanelen', icon: Sun, color: 'bg-amber-500' },
  { value: 'batterij', label: 'Batterij', icon: Battery, color: 'bg-green-500' },
  { value: 'laadpaal', label: 'Laadpaal', icon: Car, color: 'bg-purple-500' },
  { value: 'omvormer', label: 'Omvormer', icon: Zap, color: 'bg-blue-500' },
  { value: 'combi', label: 'Combi (meerdere)', icon: Zap, color: 'bg-orange-500' },
];

const STATUS_CONFIG = {
  gepland: { label: 'Gepland', color: 'bg-blue-100 text-blue-800', icon: Calendar },
  in_uitvoering: { label: 'In uitvoering', color: 'bg-amber-100 text-amber-800', icon: Clock },
  voltooid: { label: 'Voltooid', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  geannuleerd: { label: 'Geannuleerd', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const Installaties = () => {
  const [installations, setInstallations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // For calendar hover tooltip
  const [projectsToSchedule, setProjectsToSchedule] = useState([]); // Projects with paid voorschot
  const [projectDocs, setProjectDocs] = useState({}); // offerte_id -> documenten (legplan, foto's)
  const [docsDialogProject, setDocsDialogProject] = useState(null);
  const [projectToDismiss, setProjectToDismiss] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  const handleDismissProject = async () => {
    if (!projectToDismiss) return;
    setDismissing(true);
    try {
      await api.post(`/installation-projects/${projectToDismiss.offerte_id}/dismiss`);
      setProjectsToSchedule(prev => prev.filter(p => p.offerte_id !== projectToDismiss.offerte_id));
      toast.success('Project verwijderd uit de lijst');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon project niet verwijderen');
    } finally {
      setDismissing(false);
      setProjectToDismiss(null);
    }
  };
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [installationToDelete, setInstallationToDelete] = useState(null);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [activeTab, setActiveTab] = useState('te_plannen');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null); // For project details dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false); // For scheduling from project
  const [scheduleMode, setScheduleMode] = useState('montage'); // 'montage' | 'aansluiting'
  const [scheduleQueueMail, setScheduleQueueMail] = useState(true);
  const [remarkProject, setRemarkProject] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  const [remarkSaving, setRemarkSaving] = useState(false);

  const saveProjectRemarks = async () => {
    setRemarkSaving(true);
    try {
      await api.put(`/installation-offertes/${remarkProject.offerte_id}/project-remarks`, { remarks: remarkText });
      setProjectsToSchedule(prev => prev.map(p => p.offerte_id === remarkProject.offerte_id ? { ...p, project_remarks: remarkText.trim() } : p));
      toast.success('Opmerkingen opgeslagen');
      setRemarkProject(null);
    } catch (e) {
      toast.error('Kon opmerkingen niet opslaan');
    } finally {
      setRemarkSaving(false);
    }
  };
  
  // Customer details popup
  const [customerPopupOpen, setCustomerPopupOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  
  // Email confirmation state
  const [installationEmailAction, setInstallationEmailAction] = useState('queue'); // 'queue', 'send', or 'none'
  const [installationCcEmails, setInstallationCcEmails] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    property_id: '',
    installation_type: 'zonnepanelen',
    installation_date: '',
    installation_end_date: '',
    installation_time: '09:00',
    installation_end_time: '17:00',
    description: '',
    notes: '',
    status: 'gepland',
    assigned_to: [],  // Array of user IDs
    assigned_to_names: [],  // Array of names
  });
  
  const [users, setUsers] = useState([]);
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is cleaner (Install/Clean role)
  const isCleaner = user?.role === 'cleaner';
  
  // Set default tab to 'gepland' for cleaners (they don't have te_plannen tab)
  useEffect(() => {
    if (isCleaner && activeTab === 'te_plannen') {
      setActiveTab('gepland');
    }
  }, [isCleaner, activeTab]);
  
  // Photo upload state for completion
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [installationToComplete, setInstallationToComplete] = useState(null);
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [lightboxDoc, setLightboxDoc] = useState(null); // Popup viewer voor foto/document
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  
  // Cancel/Annuleer dialog state for Install/Clean users
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [installationToCancel, setInstallationToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Add note/opmerking dialog state for Install/Clean users  
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [installationForNote, setInstallationForNote] = useState(null);
  const [newNote, setNewNote] = useState('');

  const fetchData = async () => {
    try {
      const [instRes, propsRes, custRes, apptRes, projectsRes, usersRes] = await Promise.all([
        api.get('/scheduled-installations'),
        propertyAPI.getAll(),
        customerAPI.getAll(),
        appointmentAPI.getAll(),
        api.get('/installation-projects/to-schedule'),
        api.get('/users').catch(() => ({ data: [] })),  // Get users for assignment dropdown
      ]);
      
      setInstallations(instRes.data || []);
      setProperties(propsRes.data || []);
      setCustomers(custRes.data || []);
      setAllAppointments(apptRes.data || []);
      const projects = projectsRes.data || [];
      setProjectsToSchedule(projects);
      // Laad offerte-documenten (legplan, foto's) voor te plannen projecten ÉN geplande installaties
      const offerteIds = [...new Set([
        ...projects.map(p => p.offerte_id),
        ...(instRes.data || []).map(i => i.offerte_id)
      ].filter(Boolean))];
      if (offerteIds.length > 0) {
        api.post('/installation-offertes/project-documents/batch', { offerte_ids: offerteIds })
          .then(r => setProjectDocs(r.data || {}))
          .catch(() => setProjectDocs({}));
      } else {
        setProjectDocs({});
      }      // Filter to technicians and cleaners for assignment
      const assignableUsers = (usersRes.data || []).filter(u => 
        ['technician', 'cleaner', 'admin'].includes(u.role)
      );
      setUsers(assignableUsers);
    } catch (error) {
      console.error('Error fetching data:', error);
      // If endpoint doesn't exist yet, just set empty
      setInstallations([]);
      setProperties([]);
      setCustomers([]);
      setProjectsToSchedule([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const refreshProjectDocs = async (offerteId) => {
    if (!offerteId) return;
    try {
      const r = await api.get(`/installation-offertes/${offerteId}/project-documents`);
      setProjectDocs(prev => ({ ...prev, [offerteId]: r.data || [] }));
    } catch (e) {
      // stil falen; docs blijven zoals ze waren
    }
  };

  const hideProjectDoc = async (offerteId, docId) => {
    try {
      await api.post(`/installation-offertes/${offerteId}/project-documents/hide`, { doc_id: docId });
      toast.success('Uit project verwijderd (blijft bewaard in de klantmap)');
      refreshProjectDocs(offerteId);
    } catch (e) {
      toast.error('Kon niet verwijderen uit project');
    }
  };

  const getCustomerForProperty = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return null;
    return customers.find(c => c.id === property.customer_id);
  };

  const getPropertyAddress = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return 'Onbekend adres';
    return `${property.street} ${property.number}, ${property.postal_code} ${property.city}`;
  };

  const handleOpenDialog = (installation = null) => {
    if (installation) {
      setEditingInstallation(installation);
      // Get customer_id - first try from installation directly, then from property
      let customerId = installation.customer_id;
      if (!customerId && installation.property_id) {
        const property = properties.find(p => p.id === installation.property_id);
        customerId = property?.customer_id || '';
      }
      
      // Get property_id - if not set, try to find from customer's properties
      let propertyId = installation.property_id;
      if (!propertyId && customerId) {
        const customerProperty = properties.find(p => p.customer_id === customerId);
        propertyId = customerProperty?.id || '';
      }
      
      setFormData({
        customer_id: customerId || '',
        property_id: propertyId || '',
        installation_type: installation.installation_type || 'zonnepanelen',
        installation_date: installation.installation_date || '',
        installation_end_date: installation.installation_end_date || '',
        installation_time: installation.installation_time || '09:00',
        installation_end_time: installation.installation_end_time || '17:00',
        description: installation.description || '',
        notes: installation.notes || '',
        status: installation.status || 'gepland',
        assigned_to: installation.assigned_to || [],
        assigned_to_names: installation.assigned_to_names || [],
      });
    } else {
      setEditingInstallation(null);
      setFormData({
        customer_id: '',
        property_id: '',
        installation_type: 'zonnepanelen',
        installation_date: '',
        installation_end_date: '',
        installation_time: '09:00',
        installation_end_time: '17:00',
        description: '',
        notes: '',
        status: 'gepland',
        assigned_to: [],
        assigned_to_names: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.property_id) {
      toast.error('Selecteer een adres');
      return;
    }
    if (!formData.installation_date) {
      toast.error('Selecteer een datum');
      return;
    }

    setSaving(true);
    try {
      let installationId;
      
      if (editingInstallation) {
        await api.put(`/scheduled-installations/${editingInstallation.id}`, formData);
        installationId = editingInstallation.id;
        toast.success('Installatie bijgewerkt');
      } else {
        const response = await api.post('/scheduled-installations', formData);
        installationId = response.data?.id;
        toast.success('Installatie ingepland');
        
        // Send confirmation email if requested (only for new installations)
        if (installationEmailAction !== 'none' && installationId) {
          try {
            // Create the installation confirmation email
            const emailResponse = await api.post(`/scheduled-installations/${installationId}/send-confirmation`, {
              cc_emails: installationCcEmails
            });
            
            const emailId = emailResponse.data?.email_id;
            
            // If action is 'send', send immediately via email-queue endpoint
            if (installationEmailAction === 'send' && emailId) {
              try {
                await api.post(`/email-queue/${emailId}/send`);
                toast.success('Bevestigingsmail verzonden!');
              } catch (sendError) {
                console.error('Error sending email:', sendError);
                toast.success('Bevestigingsmail klaargezet in wachtrij (kon niet direct verzenden)');
              }
            } else {
              toast.success('Bevestigingsmail klaargezet in wachtrij');
            }
          } catch (emailError) {
            console.error('Error creating confirmation email:', emailError);
            // Don't fail the whole operation if email fails
          }
        }
      }
      
      setDialogOpen(false);
      setInstallationEmailAction('queue'); // Reset
      setInstallationCcEmails([]); // Reset
      fetchData();
    } catch (error) {
      console.error('Error saving installation:', error);
      toast.error('Kon installatie niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!installationToDelete) return;
    
    try {
      await api.delete(`/scheduled-installations/${installationToDelete.id}`);
      toast.success('Installatie verwijderd');
      setDeleteConfirmOpen(false);
      setInstallationToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting installation:', error);
      toast.error('Kon installatie niet verwijderen');
    }
  };

  const handleStatusChange = async (installation, newStatus) => {
    try {
      await api.put(`/scheduled-installations/${installation.id}`, {
        ...installation,
        status: newStatus
      });
      toast.success(`Status gewijzigd naar ${STATUS_CONFIG[newStatus].label}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Kon status niet wijzigen');
    }
  };

  // Open completion dialog with photo upload
  const handleOpenCompletionDialog = (installation, photosOnly = false) => {
    setInstallationToComplete({ ...installation, photosOnly });
    setCompletionPhotos([]);
    setCompletionDialogOpen(true);
  };

  // Handle photo selection from file input
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setCompletionPhotos(prev => [...prev, ...newPhotos]);
  };

  // Remove a photo from the list
  const handleRemovePhoto = (index) => {
    setCompletionPhotos(prev => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  // Upload photos and complete installation
  const handleCompleteWithPhotos = async () => {
    if (!installationToComplete) return;
    
    setUploadingPhotos(true);
    try {
      // Upload photos if any
      if (completionPhotos.length > 0) {
        for (const photo of completionPhotos) {
          const formData = new FormData();
          formData.append('file', photo.file);
          formData.append('installation_id', installationToComplete.id);
          formData.append('customer_id', installationToComplete.customer_id || '');
          formData.append('description', `Installatie foto - ${installationToComplete.customer_name || 'Onbekend'}`);
          
          await api.post('/scheduled-installations/upload-photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success(`${completionPhotos.length} foto('s) geüpload`);
      }
      
      // Mark as completed if not photos-only mode
      if (!installationToComplete.photosOnly) {
        await api.put(`/scheduled-installations/${installationToComplete.id}`, {
          ...installationToComplete,
          status: 'voltooid'
        });
        toast.success('Installatie gemarkeerd als voltooid');
      }
      
      // Cleanup
      completionPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      setCompletionPhotos([]);
      setCompletionDialogOpen(false);
      setInstallationToComplete(null);
      fetchData();
    } catch (error) {
      console.error('Error completing installation:', error);
      toast.error('Kon installatie niet voltooien');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Open cancel dialog for Install/Clean users
  const handleOpenCancelDialog = (installation) => {
    setInstallationToCancel(installation);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  // Handle cancel/annuleer installation
  const handleCancelInstallation = async () => {
    if (!installationToCancel || !cancelReason.trim()) {
      toast.error('Vul een reden in voor de annulering');
      return;
    }
    
    try {
      await api.put(`/scheduled-installations/${installationToCancel.id}`, {
        ...installationToCancel,
        status: 'geannuleerd',
        cancel_reason: cancelReason,
        cancelled_by: user?.id,
        cancelled_by_name: `${user?.first_name} ${user?.last_name}`,
        cancelled_at: new Date().toISOString()
      });
      toast.success('Installatie geannuleerd');
      setCancelDialogOpen(false);
      setInstallationToCancel(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      console.error('Error cancelling installation:', error);
      toast.error('Kon installatie niet annuleren');
    }
  };

  // Open note dialog for Install/Clean users
  const handleOpenNoteDialog = (installation) => {
    setInstallationForNote(installation);
    setNewNote(installation.notes || '');
    setNoteDialogOpen(true);
  };

  // Handle adding/updating note
  const handleSaveNote = async () => {
    if (!installationForNote) return;
    
    try {
      await api.put(`/scheduled-installations/${installationForNote.id}`, {
        ...installationForNote,
        notes: newNote
      });
      toast.success('Opmerking opgeslagen');
      setNoteDialogOpen(false);
      setInstallationForNote(null);
      setNewNote('');
      fetchData();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Kon opmerking niet opslaan');
    }
  };

  const showCustomerDetails = (propertyId) => {
    const customer = getCustomerForProperty(propertyId);
    const property = properties.find(p => p.id === propertyId);
    if (customer) {
      setSelectedCustomerData({ customer, property });
      setCustomerPopupOpen(true);
    }
  };

  // Filter installations by status
  const searchQuery = searchTerm.trim().toLowerCase();
  const matchesSearch = (obj) => !searchQuery || [
    obj.customer_name, obj.offerte_number, obj.address, obj.property_street,
    obj.property_city, obj.description, obj.customer_email, obj.customer_phone
  ].filter(Boolean).join(' ').toLowerCase().includes(searchQuery);
  
  const visibleProjects = projectsToSchedule.filter(matchesSearch);
  
  // Tab-indeling: gepland met toekomstige datum blijft bij "In te plannen" tot de installatiedag
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const getInstTab = (inst) => {
    if (inst.status === 'voltooid') return 'voltooid';
    if (inst.status === 'in_uitvoering') return 'in_uitvoering';
    if (inst.status === 'gepland') {
      return (inst.installation_date && String(inst.installation_date).slice(0, 10) <= todayStr) ? 'in_uitvoering' : 'ingepland';
    }
    return null;
  };
  // Aansluiting-omvormer records met montage-broer tonen we niet als aparte tegel (datum staat op fase 2)
  const isAansluitingSibling = (inst) => !isCleaner && inst.installation_type === 'omvormer' && inst.offerte_id &&
    installations.some(i => i.id !== inst.id && i.offerte_id === inst.offerte_id && i.installation_type !== 'omvormer' && i.status !== 'geannuleerd');
  const getAansluitingSibling = (inst) => inst.offerte_id
    ? installations.find(i => i.id !== inst.id && i.offerte_id === inst.offerte_id && i.installation_type === 'omvormer' && i.status !== 'geannuleerd')
    : null;
  
  const filteredInstallations = installations.filter(inst => {
    if (!matchesSearch(inst)) return false;
    // For cleaners, "gepland" tab shows both gepland AND in_uitvoering
    if (isCleaner && activeTab === 'gepland') {
      return inst.status === 'gepland' || inst.status === 'in_uitvoering';
    }
    if (isCleaner) return inst.status === activeTab;
    if (isAansluitingSibling(inst)) return false;
    return getInstTab(inst) === activeTab;
  });

  // Stats
  const stats = {
    te_plannen: installations.filter(i => getInstTab(i) === 'te_plannen' && !isAansluitingSibling(i)).length,
    ingepland: installations.filter(i => getInstTab(i) === 'ingepland' && !isAansluitingSibling(i)).length,
    gepland: installations.filter(i => i.status === 'gepland').length,
    in_uitvoering: installations.filter(i => getInstTab(i) === 'in_uitvoering' && !isAansluitingSibling(i)).length,
    voltooid: installations.filter(i => i.status === 'voltooid' && !isAansluitingSibling(i)).length,
    geannuleerd: installations.filter(i => i.status === 'geannuleerd').length,
    total: installations.length,
  };
  
  // Geannuleerde installaties die opnieuw ingepland moeten worden (alleen voor admin)
  const cancelledInstallations = installations.filter(i => i.status === 'geannuleerd');

  const getTypeInfo = (type) => {
    return INSTALLATION_TYPES.find(t => t.value === type) || INSTALLATION_TYPES[0];
  };

  // Build customer options for SearchSelect
  const customerOptions = customers.map(cust => ({
    value: cust.id,
    label: `${cust.first_name} ${cust.last_name}`,
    email: cust.email || ''
  }));

  // Build property options for SearchSelect - filter by selected customer if any
  const propertyOptions = properties
    .filter(prop => !formData.customer_id || prop.customer_id === formData.customer_id)
    .map(prop => {
      const customer = customers.find(c => c.id === prop.customer_id);
      return {
        value: prop.id,
        label: `${prop.street} ${prop.number}, ${prop.postal_code} ${prop.city}`,
        customer: customer ? `${customer.first_name} ${customer.last_name}` : '',
        customer_id: prop.customer_id
      };
    });

  // Helper for date formatting (local timezone)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper for calendar hover tooltip
  const getAppointmentsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return allAppointments.filter(a => a.date === dateStr);
  };

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}u${mins}` : `${hours}u`;
    }
    return `${minutes}min`;
  };

  // Helper to get city from appointment
  const getAppointmentCity = (appointment) => {
    if (appointment.property_id) {
      const prop = properties.find(p => p.id === appointment.property_id);
      if (prop?.city) return prop.city;
    }
    if (appointment.customer_id) {
      const cust = customers.find(c => c.id === appointment.customer_id);
      if (cust?.city) return cust.city;
    }
    return null;
  };

  // Custom day content with hover tooltip
  const DayWithTooltip = ({ date }) => {
    const dayAppointments = getAppointmentsForDate(date);
    const hasAppointments = dayAppointments.length > 0;
    
    if (!hasAppointments) {
      return <span>{date.getDate()}</span>;
    }

    const cleaningsForDay = dayAppointments.filter(a => a.appointment_type === 'cleaning');
    const sales = dayAppointments.filter(a => a.appointment_type === 'sales');
    const installationsForDay = dayAppointments.filter(a => a.appointment_type === 'installation');

    // Render appointment info with duration and city
    const renderAppointment = (a, i) => {
      const duration = formatDuration(a.duration_minutes);
      const city = getAppointmentCity(a);
      const timeStr = a.end_time ? `${a.time}-${a.end_time}` : a.time;
      return (
        <div key={i} className="text-xs pl-4 text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">{timeStr}</span>
            {duration && <span className="text-muted-foreground/70">({duration})</span>}
            <span>-</span>
            <span>{a.customer_name || a.title}</span>
            {city && <span className="text-blue-400/80">• {city}</span>}
          </div>
          {a.assigned_to_name && <div className="text-primary/70">→ {a.assigned_to_name}</div>}
        </div>
      );
    };

    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-pointer font-bold">{date.getDate()}</span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-3" side="right" align="start">
          <div className="space-y-2">
            <p className="font-semibold text-sm border-b pb-1">
              {format(date, 'd MMMM yyyy', { locale: nl })}
            </p>
            <p className="text-xs text-muted-foreground">{dayAppointments.length} afspraken</p>
            
            {cleaningsForDay.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-purple-400">
                  <Sparkles className="w-3 h-3" />
                  Reinigingen ({cleaningsForDay.length})
                </div>
                {cleaningsForDay.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {cleaningsForDay.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{cleaningsForDay.length - 3} meer...</div>}
              </div>
            )}
            
            {sales.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-blue-400">
                  <Users className="w-3 h-3" />
                  Sales ({sales.length})
                </div>
                {sales.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {sales.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{sales.length - 3} meer...</div>}
              </div>
            )}
            
            {installationsForDay.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-amber-400">
                  <Wrench className="w-3 h-3" />
                  Installaties ({installationsForDay.length})
                </div>
                {installationsForDay.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {installationsForDay.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{installationsForDay.length - 3} meer...</div>}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4 sm:space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Installaties</h1>
          {/* Hide subtitle for Install/Clean users */}
          {!isCleaner && (
            <p className="text-sm text-muted-foreground">
              Plan en beheer installaties
            </p>
          )}
        </div>
        {/* Hide "Nieuw" button for Install/Clean users */}
        {!isCleaner && (
          <Button onClick={() => handleOpenDialog()} data-testid="new-installation-btn" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Nieuwe Installatie</span>
            <span className="sm:hidden">Nieuw</span>
          </Button>
        )}
      </div>

      {/* Stats Cards - Limited for Install/Clean */}
      {isCleaner ? (
        <div className="grid grid-cols-1 gap-2 sm:gap-4">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('gepland')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Geplande Installaties</p>
                  <p className="text-xl sm:text-2xl font-bold">{stats.gepland + stats.in_uitvoering}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('te_plannen')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">In te plannen</p>
                  <p className="text-xl sm:text-2xl font-bold">{visibleProjects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('ingepland')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Ingepland</p>
                  <p className="text-xl sm:text-2xl font-bold">{stats.ingepland}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('in_uitvoering')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">In uitvoering</p>
                  <p className="text-xl sm:text-2xl font-bold">{stats.in_uitvoering}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('voltooid')}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">Voltooid</p>
                  <p className="text-xl sm:text-2xl font-bold">{stats.voltooid}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Geannuleerde Installaties Alert - Alleen voor Admin */}
      {!isCleaner && cancelledInstallations.length > 0 && (
        <Card className="border-red-500/50 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-600 flex items-center gap-2">
                  Geannuleerde Installaties ({cancelledInstallations.length})
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Deze installaties zijn geannuleerd door een medewerker en moeten opnieuw worden ingepland.
                </p>
                <div className="space-y-2">
                  {cancelledInstallations.map(installation => {
                    const customer = getCustomerForProperty(installation.property_id);
                    const property = properties.find(p => p.id === installation.property_id);
                    const customerName = customer 
                      ? `${customer.first_name} ${customer.last_name}` 
                      : (installation.customer_name || 'Onbekende klant');
                    
                    return (
                      <div 
                        key={installation.id} 
                        className="flex items-center justify-between p-3 bg-background rounded-lg border border-red-200"
                      >
                        <div className="flex items-center gap-3">
                          <XCircle className="h-4 w-4 text-red-500" />
                          <div>
                            <p className="font-medium">{customerName}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {property && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {property.street} {property.number}, {property.city}
                                </span>
                              )}
                              {installation.cancelled_by_name && (
                                <span className="text-red-500">
                                  Geannuleerd door: {installation.cancelled_by_name}
                                </span>
                              )}
                            </div>
                            {installation.cancel_reason && (
                              <p className="text-xs text-red-500 mt-1">
                                <strong>Reden:</strong> {installation.cancel_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 hover:bg-red-50"
                          onClick={() => {
                            // Zet status terug naar gepland en open edit dialog
                            handleStatusChange(installation, 'gepland');
                          }}
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          Opnieuw plannen
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs - Limited for Install/Clean */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Zoek op klant, adres of offertenummer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="installations-search"
        />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="inline-flex w-auto min-w-full sm:w-auto">
            {/* Only show te_plannen for non-cleaners */}
            {!isCleaner && (
              <TabsTrigger value="te_plannen" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">In te plannen</span>
                <span className="sm:hidden">Plannen</span>
                ({visibleProjects.length})
              </TabsTrigger>
            )}
            {!isCleaner && (
              <TabsTrigger value="ingepland" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap" data-testid="tab-ingepland">
                <CalendarIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                Ingepland ({stats.ingepland})
              </TabsTrigger>
            )}
            {isCleaner && (
              <TabsTrigger value="gepland" className="text-xs sm:text-sm whitespace-nowrap">
                Geplande Installaties ({stats.gepland + stats.in_uitvoering})
              </TabsTrigger>
            )}
            {/* Only show in_uitvoering and voltooid for non-cleaners */}
            {!isCleaner && (
              <>
                <TabsTrigger value="in_uitvoering" className="text-xs sm:text-sm whitespace-nowrap">
                  <span className="hidden sm:inline">In uitvoering</span>
                  <span className="sm:hidden">Bezig</span>
                  ({stats.in_uitvoering})
                </TabsTrigger>
                <TabsTrigger value="voltooid" className="text-xs sm:text-sm whitespace-nowrap">Voltooid ({stats.voltooid})</TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        {/* In te plannen Tab Content - Only for non-cleaners */}
        {!isCleaner && (
          <TabsContent value="te_plannen" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : visibleProjects.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
                  <h3 className="text-lg font-medium">Geen projecten in te plannen</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    Wanneer een installatie offerte getekend (geaccepteerd) is, verschijnt het project hier om in te plannen en fases aan te vinken.
                  </p>
                </CardContent>
              </Card>
            ) : (
            <div className="space-y-3">
              {visibleProjects.map(project => (
                <Card key={project.offerte_id} className="hover:shadow-md transition-shadow border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        <div className="p-3 rounded-lg bg-amber-500">
                          <Wrench className="h-6 w-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {project.voorschot_paid ? (
                              <Badge className="bg-green-100 text-green-800">Voorschot betaald</Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-800">Offerte getekend</Badge>
                            )}
                            {(project.signed_at || (project.voorschot_paid && project.voorschot_paid_date)) && (
                              <span className="text-xs text-muted-foreground" data-testid={`project-signed-date-${project.offerte_id}`}>
                                {project.signed_at
                                  ? `getekend op ${new Date(project.signed_at).toLocaleDateString('nl-BE')}`
                                  : `voorschot betaald op ${new Date(project.voorschot_paid_date).toLocaleDateString('nl-BE')}`}
                              </span>
                            )}
                            <Badge variant="outline">Offerte {project.offerte_number}</Badge>
                          </div>
                          <h3 className="font-medium">{project.customer_name}</h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {project.property_address || 'Geen adres'}
                          </p>
                          
                          {/* Products preview */}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {project.products?.slice(0, 3).map((prod, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {prod.quantity > 1 ? `${prod.quantity}x ` : ''}{prod.name}
                              </Badge>
                            ))}
                            {project.products?.length > 3 && (
                              <Badge variant="secondary" className="text-xs">+{project.products.length - 3} meer</Badge>
                            )}
                          </div>
                          
                          {/* Legplannen & foto's van de offerte */}
                          {(projectDocs[project.offerte_id] || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`project-docs-${project.offerte_id}`}>
                              {(projectDocs[project.offerte_id] || []).slice(0, 6).map((doc) => {
                                const docUrl = doc.r2_public_url || `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`;
                                const isImg = /\.(jpe?g|png|webp)$/i.test(doc.filename || '');
                                return (
                                  <div key={doc.id} className="relative group">
                                    <button
                                      type="button"
                                      title={doc.filename}
                                      onClick={(e) => { e.stopPropagation(); setLightboxDoc({ url: docUrl, filename: doc.filename }); }}
                                      className="focus:outline-none focus:ring-2 focus:ring-primary rounded-md"
                                      data-testid={`project-doc-${doc.id}`}
                                    >
                                      {isImg ? (
                                        <img src={docUrl} alt={doc.filename} className="h-14 w-14 object-cover rounded-md border hover:opacity-90 transition-opacity" loading="lazy" />
                                      ) : (
                                        <span className="h-14 w-14 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-0.5 hover:bg-muted transition-colors">
                                          <FileText className="h-5 w-5 text-red-500" />
                                          <span className="text-[9px] text-muted-foreground uppercase">{(doc.filename || '').split('.').pop()}</span>
                                        </span>
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      title="Uit project verwijderen (blijft in klantmap)"
                                      onClick={(e) => { e.stopPropagation(); hideProjectDoc(project.offerte_id, doc.id); }}
                                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-background border shadow flex items-center justify-center text-muted-foreground hover:text-red-500 hover:border-red-400 transition-colors"
                                      data-testid={`project-doc-hide-${doc.id}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                );
                              })}
                              {(projectDocs[project.offerte_id] || []).length > 6 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setDocsDialogProject(project); }}
                                  className="h-14 w-14 rounded-md border bg-muted/40 flex items-center justify-center text-xs text-muted-foreground hover:bg-muted transition-colors"
                                >
                                  +{(projectDocs[project.offerte_id] || []).length - 6}
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Project opmerkingen (zichtbaar voor toegewezen install/clean accounts na inplannen) */}
                          <div className="mt-2">
                            {project.project_remarks && (
                              <p className="text-xs text-muted-foreground flex items-start gap-1" data-testid={`project-remarks-${project.offerte_id}`}>
                                <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" /> {project.project_remarks}
                              </p>
                            )}
                            <button
                              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
                              onClick={() => { setRemarkProject(project); setRemarkText(project.project_remarks || ''); }}
                              data-testid={`project-remarks-btn-${project.offerte_id}`}>
                              <MessageSquare className="h-3 w-3" /> {project.project_remarks ? 'Opmerkingen bewerken' : 'Opmerkingen toevoegen'}
                            </button>
                          </div>
                          
                          {/* Contact info */}
                          {(project.customer_phone || project.customer_email) && (
                            <div className="mt-2 flex gap-3 text-xs text-muted-foreground">
                              {project.customer_phone && (
                                <a
                                  href={`tel:${project.customer_phone.replace(/\s/g, '')}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1 text-primary hover:underline"
                                  data-testid="project-card-phone-link"
                                >
                                  <Phone className="h-3 w-3" />
                                  {project.customer_phone}
                                </a>
                              )}
                              {project.customer_email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {project.customer_email}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setDocsDialogProject(project)}
                          title="Legplannen & foto's bekijken of toevoegen"
                          data-testid={`project-photos-btn-${project.offerte_id}`}
                        >
                          <Camera className="h-4 w-4 mr-1" />
                          Foto's
                          {(projectDocs[project.offerte_id] || []).length > 0 && (
                            <span className="ml-1 text-xs text-muted-foreground">({(projectDocs[project.offerte_id] || []).length})</span>
                          )}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedProject(project)}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => {
                            setScheduleMode('montage');
                            setSelectedProject(project);
                            setScheduleDialogOpen(true);
                          }}
                          data-testid={`plan-montage-${project.offerte_id}`}
                        >
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Plaatsing inplannen</span>
                          <span className="sm:hidden">Plaatsing</span>
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setScheduleMode('aansluiting');
                            setSelectedProject(project);
                            setScheduleDialogOpen(true);
                          }}
                          data-testid={`plan-aansluiting-project-${project.offerte_id}`}
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          <span className="hidden sm:inline">Aansluiting inplannen</span>
                          <span className="sm:hidden">Aansluiting</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                          title="Verwijder uit 'In te plannen' lijst"
                          onClick={() => setProjectToDismiss(project)}
                          data-testid={`dismiss-project-${project.offerte_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          </TabsContent>
        )}

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredInstallations.length === 0 ? (
            activeTab === 'te_plannen' ? null : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Zap className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Geen installaties gevonden</h3>
                <p className="text-muted-foreground">
                  {isCleaner ? 'Er zijn nog geen installaties aan jou toegewezen' : activeTab === 'ingepland' ? 'Geen ingeplande installaties die op hun installatiedag wachten' : `Geen installaties met status "${STATUS_CONFIG[activeTab]?.label || 'deze status'}"`}
                </p>
                {!isCleaner && (
                  <Button className="mt-4" onClick={() => handleOpenDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nieuwe Installatie
                  </Button>
                )}
              </CardContent>
            </Card>
            )
          ) : (
            <div className="space-y-3">
              {activeTab === 'ingepland' && !isCleaner && (
                <p className="text-sm font-medium text-muted-foreground mt-2">Gepland — wacht op installatiedag ({filteredInstallations.length})</p>
              )}
              {filteredInstallations.map(installation => {
                const customer = getCustomerForProperty(installation.property_id);
                const property = properties.find(p => p.id === installation.property_id);
                const typeInfo = getTypeInfo(installation.installation_type);
                const TypeIcon = typeInfo.icon;
                const statusConfig = STATUS_CONFIG[installation.status] || STATUS_CONFIG.gepland;
                
                // Get customer name - try multiple sources
                const customerName = customer 
                  ? `${customer.first_name} ${customer.last_name}` 
                  : (installation.customer_name || 'Onbekende klant');
                
                // Get address - try multiple sources
                const addressDisplay = property 
                  ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}`
                  : (installation.property_street && installation.property_city 
                      ? `${installation.property_street}, ${installation.property_city}` 
                      : 'Onbekend adres');

                return (
                  <Card key={installation.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex gap-3 sm:gap-4 min-w-0 flex-1">
                          <div className={`p-2.5 sm:p-3 rounded-lg flex-shrink-0 self-start ${typeInfo.color}`}>
                            <TypeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={statusConfig.color}>
                                {statusConfig.label}
                              </Badge>
                              <Badge variant="outline">{typeInfo.label}</Badge>
                            </div>
                            {/* Only make clickable for non-cleaners */}
                            {isCleaner ? (
                              <h3 className="font-medium">
                                {customerName}
                              </h3>
                            ) : (
                              <h3 
                                className="font-medium cursor-pointer hover:text-primary"
                                onClick={() => showCustomerDetails(installation.property_id)}
                              >
                                {customerName}
                              </h3>
                            )}
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {addressDisplay}
                            </p>
                            {installation.description && (
                              <p className="text-sm mt-1">{installation.description}</p>
                            )}
                            {/* Show notes/opmerkingen from admin */}
                            {installation.notes && (
                              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-md">
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                  <span className="font-medium">Opmerkingen: </span>
                                  {installation.notes}
                                </p>
                              </div>
                            )}
                            {/* Photo attachments (legplan, ...) */}
                            {installation.photos?.length > 0 && (
                              <div className="mt-2" data-testid="installation-card-photos">
                                <p className="text-xs font-semibold text-blue-500 flex items-center gap-1 mb-1">
                                  <Camera className="h-3 w-3" />
                                  Foto's & documenten ({installation.photos.length})
                                </p>
                                <div className="flex gap-2 flex-wrap">
                                  {installation.photos.map(p => (
                                    <button
                                      key={p.id}
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setLightboxDoc({ url: p.url, filename: p.filename || 'Foto' }); }}
                                      className="focus:outline-none"
                                      data-testid={`installation-photo-${p.id}`}
                                    >
                                      {/\.pdf($|\?)/i.test(p.url || '') || /\.pdf$/i.test(p.filename || '') ? (
                                        <div className="h-16 w-16 rounded-md border bg-muted/40 flex items-center justify-center hover:bg-muted transition-colors">
                                          <FileText className="w-6 h-6 text-red-500" />
                                        </div>
                                      ) : (
                                        <img
                                          src={p.url}
                                          alt={p.description || p.filename || 'Foto'}
                                          className="h-16 w-16 object-cover rounded-md border hover:opacity-80 transition-opacity"
                                        />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Plannen & bijlagen uit de offerte — blijven zichtbaar in elke fase */}
                            {(projectDocs[installation.offerte_id] || []).length > 0 && (
                              <div className="mt-2" data-testid={`installation-offerte-docs-${installation.id}`}>
                                <p className="text-xs font-semibold text-orange-500 flex items-center gap-1 mb-1">
                                  <FileText className="h-3 w-3" />
                                  Plannen uit offerte ({(projectDocs[installation.offerte_id] || []).length})
                                </p>
                                <div className="flex gap-2 flex-wrap">
                                  {(projectDocs[installation.offerte_id] || []).map(doc => {
                                    const docUrl = doc.r2_public_url || `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`;
                                    const isPdf = /\.pdf$/i.test(doc.filename || '');
                                    return (
                                      <div key={doc.id} className="relative group">
                                        <button
                                          type="button"
                                          title={doc.filename}
                                          onClick={(e) => { e.stopPropagation(); setLightboxDoc({ url: docUrl, filename: doc.filename || 'Document' }); }}
                                          className="focus:outline-none"
                                          data-testid={`installation-offerte-doc-${doc.id}`}
                                        >
                                          {isPdf ? (
                                            <div className="h-16 w-16 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-0.5 hover:bg-muted transition-colors">
                                              <FileText className="w-5 h-5 text-red-500" />
                                              <span className="text-[9px] text-muted-foreground uppercase">PDF</span>
                                            </div>
                                          ) : (
                                            <img
                                              src={docUrl}
                                              alt={doc.filename || 'Plan'}
                                              className="h-16 w-16 object-cover rounded-md border hover:opacity-80 transition-opacity"
                                              loading="lazy"
                                            />
                                          )}
                                        </button>
                                        {!isCleaner && (
                                          <button
                                            type="button"
                                            title="Uit project verwijderen (blijft in klantmap)"
                                            onClick={(e) => { e.stopPropagation(); hideProjectDoc(installation.offerte_id, doc.id); }}
                                            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-background border shadow flex items-center justify-center text-muted-foreground hover:text-red-500 hover:border-red-400 transition-colors"
                                            data-testid={`installation-doc-hide-${doc.id}`}
                                          >
                                            <X className="h-3 w-3" />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {!isCleaner && (
                                    <button
                                      type="button"
                                      title="Foto's of plannen toevoegen"
                                      onClick={(e) => { e.stopPropagation(); setDocsDialogProject({ offerte_id: installation.offerte_id, offerte_number: installation.offerte_number }); }}
                                      className="h-16 w-16 rounded-md border border-dashed flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                                      data-testid={`installation-add-offerte-doc-${installation.id}`}
                                    >
                                      <Upload className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Geen offerte-docs maar wel offerte: knop om toe te voegen */}
                            {installation.offerte_id && (projectDocs[installation.offerte_id] || []).length === 0 && !isCleaner && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setDocsDialogProject({ offerte_id: installation.offerte_id, offerte_number: installation.offerte_number }); }}
                                className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground border border-dashed rounded-md px-2 py-1 hover:bg-muted transition-colors"
                                data-testid={`installation-add-offerte-doc-${installation.id}`}
                              >
                                <Camera className="h-3 w-3" />
                                Plannen/foto's toevoegen
                              </button>
                            )}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3" />
                                {formatDate(installation.installation_date)}
                                {installation.installation_end_date && installation.installation_end_date !== installation.installation_date && (
                                  <span className="text-primary"> - {formatDate(installation.installation_end_date)}</span>
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {installation.installation_time || '09:00'}
                                {installation.installation_end_time && (
                                  <span> - {installation.installation_end_time}</span>
                                )}
                              </span>
                              {installation.assigned_to_names && installation.assigned_to_names.length > 0 && (
                                <span className="flex items-center gap-1 text-primary">
                                  <User className="h-3 w-3" />
                                  {installation.assigned_to_names.join(', ')}
                                </span>
                              )}
                            </div>
                            {/* Extra producten & opmerkingen */}
                            <InstallationExtras installation={installation}
                              onUpdated={(patch) => setInstallations(prev => prev.map(i => i.id === installation.id ? { ...i, ...patch } : i))} />
                            {/* Projectfases (alleen admin): alle 3 aangevinkt = project automatisch Voltooid */}
                            {hasRole && hasRole('admin') && (
                              <div className="mt-3 flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 p-2.5 rounded-md bg-muted/50 border" data-testid={`phases-${installation.id}`}>
                                {[
                                  { key: 'montage', label: 'Fase 1: Installatie' },
                                  { key: 'aansluiting', label: 'Fase 2: Aansluiting omvormer' },
                                  { key: 'keuring', label: 'Fase 3: Keuring' },
                                ].map(ph => {
                                  const sibling = ph.key === 'aansluiting' ? getAansluitingSibling(installation) : null;
                                  return (
                                  <div key={ph.key} className="flex items-center gap-1.5 flex-wrap">
                                    <label className="flex items-center gap-1.5 cursor-pointer text-xs font-medium">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 accent-orange-500"
                                      checked={!!installation.project_phases?.[ph.key]}
                                      onChange={async (e) => {
                                        const checked = e.target.checked;
                                        try {
                                          const res = await api.put(`/scheduled-installations/${installation.id}/phases`, { [ph.key]: checked });
                                          const allDone = res.data?.all_completed;
                                          setInstallations(prev => prev.map(i => i.id === installation.id
                                            ? { ...i, project_phases: res.data?.project_phases || { ...(i.project_phases || {}), [ph.key]: checked }, status: allDone ? 'voltooid' : i.status }
                                            : i));
                                          (res.data?.created_facturen || []).forEach(f => {
                                            const termLabel = f.term_number === 2 ? 'termijn 2 — na montage' : 'termijn 3 — na keuring';
                                            toast.success(`Conceptfactuur ${f.factuur_number} (${termLabel}) automatisch klaargezet bij Facturen`, { duration: 6000 });
                                          });
                                          if (allDone) {
                                            toast.success('Alle fases voltooid — project automatisch naar Voltooid verplaatst');
                                          } else {
                                            toast.success(checked ? `${ph.label} aangevinkt — melding zichtbaar bij Facturen` : `${ph.label} uitgevinkt`);
                                          }
                                        } catch (err) {
                                          toast.error('Kon fase niet bijwerken');
                                        }
                                      }}
                                      data-testid={`phase-${ph.key}-${installation.id}`}
                                    />
                                    {ph.label}
                                    </label>
                                    {ph.key === 'montage' && installation.installation_date && (
                                      <span className="text-[11px] text-blue-600 font-medium">({formatDate(installation.installation_date)})</span>
                                    )}
                                    {ph.key === 'aansluiting' && sibling && (
                                      <span className="text-[11px] text-blue-600 font-medium">({formatDate(sibling.installation_date)})</span>
                                    )}
                                    {ph.key === 'aansluiting' && !sibling && installation.installation_type !== 'omvormer' && installation.status !== 'voltooid' && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 px-2 text-[11px]"
                                        data-testid={`plan-aansluiting-${installation.id}`}
                                        onClick={() => {
                                          setScheduleMode('aansluiting');
                                          setSelectedProject({
                                            offerte_id: installation.offerte_id,
                                            customer_id: installation.customer_id,
                                            property_id: installation.property_id,
                                            property_street: installation.property_street,
                                            property_city: installation.property_city,
                                            property_address: getPropertyAddress(installation.property_id),
                                            customer_name: installation.customer_name || (getCustomerForProperty(installation.property_id) ? `${getCustomerForProperty(installation.property_id).first_name} ${getCustomerForProperty(installation.property_id).last_name}` : ''),
                                            products: installation.products || [],
                                          });
                                          setScheduleDialogOpen(true);
                                        }}
                                      >
                                        <CalendarIcon className="h-3 w-3 mr-1" />
                                        Inplannen
                                      </Button>
                                    )}
                                    {ph.key === 'keuring' && (
                                      <KeuringStatusControl installation={installation} onUpdated={(kr) => setInstallations(prev => prev.map(i => i.id === installation.id ? { ...i, keuring_request: kr } : i))} />
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="flex-shrink-0" data-testid={`installation-menu-${installation.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Only show edit for non-cleaners */}
                            {!isCleaner && (
                              <>
                                <DropdownMenuItem onClick={() => handleOpenDialog(installation)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Bewerken
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            
                            {/* Status change options - gepland/in_uitvoering only for non-cleaners */}
                            {!isCleaner && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(installation, 'gepland')}>
                                  <CalendarIcon className="h-4 w-4 mr-2" />
                                  Markeer als gepland
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(installation, 'in_uitvoering')}>
                                  <Clock className="h-4 w-4 mr-2" />
                                  Markeer als in uitvoering
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleOpenCompletionDialog(installation)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Markeer als voltooid
                            </DropdownMenuItem>
                            
                            {/* Photo upload option */}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleOpenCompletionDialog(installation, true)}>
                              <Camera className="h-4 w-4 mr-2" />
                              Foto's toevoegen
                            </DropdownMenuItem>
                            
                            {/* Note/Opmerking option for Install/Clean users */}
                            {isCleaner && (
                              <DropdownMenuItem onClick={() => handleOpenNoteDialog(installation)}>
                                <FileText className="h-4 w-4 mr-2" />
                                Opmerking toevoegen
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            {property && (
                              <>
                                <DropdownMenuItem onClick={() => window.open(getGoogleMapsUrl(property), '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Google Maps
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(getWazeUrl(property), '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Waze
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Cancel option for Install/Clean users */}
                            {isCleaner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleOpenCancelDialog(installation)}
                                  className="text-destructive"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Annuleren
                                </DropdownMenuItem>
                              </>
                            )}
                            
                            {/* Only show delete for non-cleaners */}
                            {!isCleaner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setInstallationToDelete(installation);
                                    setDeleteConfirmOpen(true);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Verwijderen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Bevestiging: project uit 'In te plannen' verwijderen */}
      <AlertDialog open={!!projectToDismiss} onOpenChange={(open) => !open && setProjectToDismiss(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Project verwijderen uit lijst?</AlertDialogTitle>
            <AlertDialogDescription>
              "{projectToDismiss?.customer_name}" (offerte {projectToDismiss?.offerte_number}) wordt uit de "In te plannen" lijst verwijderd. De offerte zelf blijft gewoon bestaan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={dismissing}
              onClick={handleDismissProject}
              data-testid="confirm-dismiss-project"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New/Edit Installation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingInstallation ? 'Installatie Bewerken' : 'Nieuwe Installatie Inplannen'}
            </DialogTitle>
            <DialogDescription>
              Plan een nieuwe installatie in voor een klant
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Bij bewerken: toon huidige klant/adres info als er geen property_id is */}
            {editingInstallation && !formData.property_id && (editingInstallation.customer_name || editingInstallation.property_street) && (
              <div className="rounded-lg border bg-muted/30 p-3 mb-2">
                <p className="text-sm text-muted-foreground mb-1">Huidige klant/adres:</p>
                <p className="font-medium">{editingInstallation.customer_name || 'Onbekende klant'}</p>
                {(editingInstallation.property_street || editingInstallation.property_city) && (
                  <p className="text-sm text-muted-foreground">
                    {editingInstallation.property_street}{editingInstallation.property_city ? `, ${editingInstallation.property_city}` : ''}
                  </p>
                )}
                <p className="text-xs text-amber-600 mt-2">
                  💡 Selecteer een klant hieronder om het adres te koppelen aan een bekende klant
                </p>
              </div>
            )}
            
            {/* Klant selectie */}
            <div className="space-y-2">
              <Label>Klant</Label>
              <SearchSelect
                options={customerOptions}
                value={formData.customer_id}
                onChange={(id) => setFormData({ ...formData, customer_id: id, property_id: '' })}
                placeholder={editingInstallation?.customer_name || "Zoek klant op naam..."}
                displayKey="label"
                valueKey="value"
                renderOption={(opt) => (
                  <div>
                    <div className="font-medium">{opt.label}</div>
                    {opt.email && (
                      <div className="text-xs text-muted-foreground">{opt.email}</div>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Adres selectie */}
            <div className="space-y-2">
              <Label>Adres {!editingInstallation?.property_street && '*'}</Label>
              <SearchSelect
                options={propertyOptions}
                value={formData.property_id}
                onChange={(id) => {
                  const selectedProp = propertyOptions.find(p => p.value === id);
                  setFormData({ 
                    ...formData, 
                    property_id: id,
                    customer_id: selectedProp?.customer_id || formData.customer_id
                  });
                }}
                placeholder={editingInstallation?.property_street 
                  ? `${editingInstallation.property_street}, ${editingInstallation.property_city}` 
                  : "Zoek op adres..."}
                displayKey="label"
                valueKey="value"
                renderOption={(opt) => (
                  <div>
                    <div className="font-medium">{opt.label}</div>
                    {opt.customer && !formData.customer_id && (
                      <div className="text-xs text-muted-foreground">{opt.customer}</div>
                    )}
                  </div>
                )}
              />
              {formData.customer_id && propertyOptions.length === 0 && (
                <p className="text-xs text-amber-500">Deze klant heeft nog geen adressen</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Type installatie</Label>
              <Select 
                value={formData.installation_type} 
                onValueChange={(v) => setFormData({ ...formData, installation_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTALLATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Datum rij */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Startdatum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.installation_date ? format(parse(formData.installation_date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.installation_date ? parse(formData.installation_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setFormData({ ...formData, installation_date: format(date, 'yyyy-MM-dd') })}
                      locale={nl}
                      initialFocus
                      components={{
                        DayContent: ({ date }) => <DayWithTooltip date={date} />
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Einddatum <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.installation_end_date ? format(parse(formData.installation_end_date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Zelfde dag'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.installation_end_date ? parse(formData.installation_end_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setFormData({ ...formData, installation_end_date: format(date, 'yyyy-MM-dd') })}
                      locale={nl}
                      disabled={(date) => formData.installation_date && date < parse(formData.installation_date, 'yyyy-MM-dd', new Date())}
                      initialFocus
                      components={{
                        DayContent: ({ date }) => <DayWithTooltip date={date} />
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Tijd rij */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Starttijd *</Label>
                <Input
                  type="time"
                  value={formData.installation_time}
                  onChange={(e) => setFormData({ ...formData, installation_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Eindtijd *</Label>
                <Input
                  type="time"
                  value={formData.installation_end_time}
                  onChange={(e) => setFormData({ ...formData, installation_end_time: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="bijv. 15 panelen + batterij 10kWh"
              />
            </div>

            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Extra opmerkingen voor de technici..."
                rows={3}
              />
            </div>

            {editingInstallation && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                      <SelectItem key={value} value={value}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Toegewezen aan - Multi-select with checkboxes */}
            <div className="space-y-2">
              <Label>Toegewezen aan</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-[150px] overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Geen technici/cleaners beschikbaar</p>
                ) : (
                  users.map(u => {
                    const isSelected = (formData.assigned_to || []).includes(u.id);
                    const roleLabel = u.role === 'cleaner' ? 'Install/Clean' : u.role;
                    return (
                      <div key={u.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`user-${u.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            const currentIds = formData.assigned_to || [];
                            const currentNames = formData.assigned_to_names || [];
                            const userName = `${u.first_name} ${u.last_name}`;
                            
                            if (checked) {
                              setFormData({
                                ...formData,
                                assigned_to: [...currentIds, u.id],
                                assigned_to_names: [...currentNames, userName]
                              });
                            } else {
                              const idx = currentIds.indexOf(u.id);
                              setFormData({
                                ...formData,
                                assigned_to: currentIds.filter(id => id !== u.id),
                                assigned_to_names: currentNames.filter((_, i) => i !== idx)
                              });
                            }
                          }}
                        />
                        <label 
                          htmlFor={`user-${u.id}`} 
                          className="text-sm cursor-pointer flex-1"
                        >
                          {u.first_name} {u.last_name}
                          <span className="text-muted-foreground ml-1">({roleLabel})</span>
                        </label>
                      </div>
                    );
                  })
                )}
              </div>
              {(formData.assigned_to || []).length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {(formData.assigned_to_names || []).join(', ')}
                </p>
              )}
            </div>
            
            {/* Bevestigingsmail naar klant - alleen bij nieuwe installaties */}
            {!editingInstallation && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Bevestigingsmail naar klant
                  </Label>
                  <Select 
                    value={installationEmailAction} 
                    onValueChange={setInstallationEmailAction}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="queue">Mail klaarzetten in wachtrij</SelectItem>
                      <SelectItem value="send">Mail direct verzenden</SelectItem>
                      <SelectItem value="none">Geen mail versturen</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {installationEmailAction === 'queue' && 'De mail wordt klaargezet in de wachtrij, u kunt deze later verzenden of bewerken'}
                    {installationEmailAction === 'send' && 'De mail wordt direct verzonden naar de klant'}
                    {installationEmailAction === 'none' && 'Er wordt geen bevestigingsmail verstuurd'}
                  </p>
                </div>
                
                {/* CC Team selector */}
                {installationEmailAction !== 'none' && (
                  <CcSelector
                    selectedEmails={installationCcEmails}
                    onChange={setInstallationCcEmails}
                    users={users}
                  />
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Opslaan...' : (editingInstallation ? 'Bijwerken' : 'Inplannen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Installatie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze installatie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Customer Details Popup */}
      <Dialog open={customerPopupOpen} onOpenChange={setCustomerPopupOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Klantgegevens</DialogTitle>
          </DialogHeader>
          {selectedCustomerData && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">
                  {selectedCustomerData.customer.first_name} {selectedCustomerData.customer.last_name}
                </h3>
              </div>
              
              {selectedCustomerData.customer.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${selectedCustomerData.customer.email}`} className="text-primary hover:underline">
                    {selectedCustomerData.customer.email}
                  </a>
                </div>
              )}
              
              {selectedCustomerData.customer.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${selectedCustomerData.customer.phone}`} className="text-primary hover:underline">
                    {formatPhoneNumber(selectedCustomerData.customer.phone)}
                  </a>
                </div>
              )}
              
              {selectedCustomerData.property && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {selectedCustomerData.property.street} {selectedCustomerData.property.number}, {selectedCustomerData.property.postal_code} {selectedCustomerData.property.city}
                  </span>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/customers/${selectedCustomerData.customer.id}`)}
                >
                  Bekijk klant
                </Button>
                {selectedCustomerData.property && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/properties/${selectedCustomerData.property.id}`)}
                  >
                    Bekijk adres
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={!!selectedProject && !scheduleDialogOpen} onOpenChange={(open) => !open && setSelectedProject(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Project Details - Offerte {selectedProject?.offerte_number}
            </DialogTitle>
            <DialogDescription>
              Voorschot betaald - klaar om in te plannen
            </DialogDescription>
          </DialogHeader>

          {selectedProject && (
            <div className="space-y-6 py-4">
              {/* Customer & Address Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">Klant</Label>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{selectedProject.customer_name}</span>
                  </div>
                  {selectedProject.customer_phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <a href={`tel:${selectedProject.customer_phone}`} className="text-primary hover:underline">
                        {selectedProject.customer_phone}
                      </a>
                    </div>
                  )}
                  {selectedProject.customer_email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <a href={`mailto:${selectedProject.customer_email}`} className="text-primary hover:underline">
                        {selectedProject.customer_email}
                      </a>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">Projectadres</Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedProject.property_address || 'Geen adres'}</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {selectedProject.property_address && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(getGoogleMapsUrl(selectedProject.property_street, selectedProject.property_city), '_blank')}
                        >
                          Google Maps
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(getWazeUrl(selectedProject.property_street, selectedProject.property_city), '_blank')}
                        >
                          Waze
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Products */}
              <div>
                <Label className="text-muted-foreground text-xs uppercase mb-2 block">Producten voor dit project</Label>
                <div className="border rounded-lg divide-y">
                  {selectedProject.products?.map((product, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sun className="h-4 w-4 text-amber-500" />
                          <span className="font-medium">
                            {product.quantity > 1 ? `${product.quantity}x ` : ''}{product.name}
                          </span>
                        </div>
                      </div>
                      {product.components && product.components.length > 0 && (
                        <div className="mt-2 ml-6 text-sm text-muted-foreground">
                          <p className="font-medium text-xs uppercase mb-1">Specificatie regels:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            {product.components.map((comp, cIdx) => (
                              <li key={cIdx}>{comp.quantity > 1 ? `${comp.quantity}x ` : ''}{comp.name}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                  {(!selectedProject.products || selectedProject.products.length === 0) && (
                    <div className="p-4 text-center text-muted-foreground">
                      Geen producten gevonden in de offerte
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedProject(null)}>
              Sluiten
            </Button>
            <Button onClick={() => setScheduleDialogOpen(true)}>
              <CalendarIcon className="h-4 w-4 mr-2" />
              Inplannen in Agenda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Installation Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={(open) => {
        setScheduleDialogOpen(open);
        if (!open) { setSelectedProject(null); setScheduleMode('montage'); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {scheduleMode === 'aansluiting' ? <Zap className="h-5 w-5" /> : <CalendarIcon className="h-5 w-5" />}
              {scheduleMode === 'aansluiting' ? 'Aansluiting omvormer inplannen' : 'Plaatsing zonnepanelen inplannen'}
            </DialogTitle>
            <DialogDescription>
              {scheduleMode === 'aansluiting' ? 'Plan de aansluiting van de omvormer voor' : 'Plan de plaatsing voor'} {selectedProject?.customer_name}
            </DialogDescription>
          </DialogHeader>

          {selectedProject && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium">{selectedProject.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selectedProject.property_address}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {selectedProject.products?.slice(0, 2).map((prod, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {prod.quantity > 1 ? `${prod.quantity}x ` : ''}{prod.name}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Startdatum *</Label>
                  <Input
                    type="date"
                    value={formData.installation_date}
                    onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Einddatum</Label>
                  <Input
                    type="date"
                    value={formData.installation_end_date}
                    onChange={(e) => setFormData({ ...formData, installation_end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Starttijd</Label>
                  <Input
                    type="time"
                    value={formData.installation_time}
                    onChange={(e) => setFormData({ ...formData, installation_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eindtijd</Label>
                  <Input
                    type="time"
                    value={formData.installation_end_time}
                    onChange={(e) => setFormData({ ...formData, installation_end_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Type installatie</Label>
                <Select 
                  value={formData.installation_type} 
                  onValueChange={(val) => setFormData({ ...formData, installation_type: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTALLATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Toegewezen aan - Multi-select with checkboxes */}
              <div className="space-y-2">
                <Label>Toegewezen aan</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-[150px] overflow-y-auto">
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Geen technici/cleaners beschikbaar</p>
                  ) : (
                    users.map(u => {
                      const isSelected = (formData.assigned_to || []).includes(u.id);
                      const roleLabel = u.role === 'cleaner' ? 'Install/Clean' : u.role;
                      return (
                        <div key={u.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`schedule-user-${u.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              const currentIds = formData.assigned_to || [];
                              const currentNames = formData.assigned_to_names || [];
                              const userName = `${u.first_name} ${u.last_name}`;
                              
                              if (checked) {
                                setFormData({
                                  ...formData,
                                  assigned_to: [...currentIds, u.id],
                                  assigned_to_names: [...currentNames, userName]
                                });
                              } else {
                                const idx = currentIds.indexOf(u.id);
                                setFormData({
                                  ...formData,
                                  assigned_to: currentIds.filter(id => id !== u.id),
                                  assigned_to_names: currentNames.filter((_, i) => i !== idx)
                                });
                              }
                            }}
                          />
                          <label 
                            htmlFor={`schedule-user-${u.id}`} 
                            className="text-sm cursor-pointer flex-1"
                          >
                            {u.first_name} {u.last_name}
                            <span className="text-muted-foreground ml-1">({roleLabel})</span>
                          </label>
                        </div>
                      );
                    })
                  )}
                </div>
                {(formData.assigned_to || []).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {(formData.assigned_to_names || []).join(', ')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Eventuele opmerkingen..."
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2 pt-2 border-t">
                <Checkbox id="schedule-queue-mail" checked={scheduleQueueMail} onCheckedChange={(c) => setScheduleQueueMail(!!c)} data-testid="schedule-queue-mail-checkbox" />
                <label htmlFor="schedule-queue-mail" className="text-sm cursor-pointer flex items-center gap-1.5">
                  <Mail className="h-4 w-4" /> Mail klaarzetten voor de klant (installatiedatum)
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={async () => {
                if (!formData.installation_date) {
                  toast.error('Selecteer een startdatum');
                  return;
                }
                
                setSaving(true);
                try {
                  // Create scheduled installation with link to offerte
                  const schedResponse = await api.post('/scheduled-installations', {
                    offerte_id: selectedProject.offerte_id,
                    customer_id: selectedProject.customer_id,
                    property_id: selectedProject.property_id,  // Include property_id from offerte
                    property_street: selectedProject.property_street,
                    property_city: selectedProject.property_city,
                    installation_type: scheduleMode === 'aansluiting' ? 'omvormer' : formData.installation_type,
                    installation_date: formData.installation_date,
                    installation_end_date: formData.installation_end_date || formData.installation_date,
                    installation_time: formData.installation_time,
                    installation_end_time: formData.installation_end_time,
                    description: scheduleMode === 'aansluiting' ? 'Aansluiting omvormer' : '',
                    notes: formData.notes,
                    status: 'gepland',
                    products: selectedProject.products,
                    assigned_to: formData.assigned_to || [],
                    assigned_to_names: formData.assigned_to_names || [],
                    customer_name: selectedProject.customer_name
                  });
                  
                  toast.success(scheduleMode === 'aansluiting' ? 'Aansluiting omvormer ingepland!' : 'Plaatsing ingepland!');
                  if (scheduleQueueMail && schedResponse.data?.id) {
                    try {
                      await api.post(`/scheduled-installations/${schedResponse.data.id}/send-confirmation`, { cc_emails: [] });
                      toast.success('Mail voor de klant klaargezet bij Mails → Klaargezet');
                    } catch (mailErr) {
                      toast.error('Installatie ingepland, maar mail kon niet klaargezet worden');
                    }
                  }
                  setScheduleDialogOpen(false);
                  setSelectedProject(null);
                  setFormData({
                    property_id: '',
                    installation_type: 'zonnepanelen',
                    installation_date: '',
                    installation_end_date: '',
                    installation_time: '09:00',
                    installation_end_time: '17:00',
                    description: '',
                    notes: '',
                    status: 'gepland',
                    assigned_to: [],
                    assigned_to_names: [],
                  });
                  fetchData(); // Refresh to remove from "In te plannen" and add to "Gepland"
                } catch (error) {
                  console.error('Error scheduling installation:', error);
                  toast.error('Fout bij inplannen');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
            >
              {saving ? 'Bezig...' : 'Inplannen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Upload / Completion Dialog */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {installationToComplete?.photosOnly ? "Foto's Toevoegen" : "Installatie Voltooien"}
            </DialogTitle>
            <DialogDescription>
              {installationToComplete?.photosOnly 
                ? `Voeg foto's toe voor ${installationToComplete?.customer_name || 'deze installatie'}`
                : `Voeg foto's toe voordat je de installatie als voltooid markeert`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Photo upload area */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handlePhotoSelect}
                className="hidden"
                id="photo-upload"
                data-testid="installation-photo-input"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Camera className="h-8 w-8 text-primary" />
                  </div>
                  <p className="font-medium">Klik om foto's of documenten te selecteren</p>
                  <p className="text-sm text-muted-foreground">afbeeldingen en PDF's toegestaan</p>
                </div>
              </label>
            </div>
            
            {/* Preview of selected photos */}
            {completionPhotos.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{completionPhotos.length} bestand(en) geselecteerd:</p>
                <div className="grid grid-cols-3 gap-2">
                  {completionPhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      {/\.pdf$/i.test(photo.name || '') ? (
                        <div className="w-full h-20 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-1">
                          <FileText className="w-6 h-6 text-red-500" />
                          <span className="text-[10px] text-muted-foreground truncate max-w-full px-1">{photo.name}</span>
                        </div>
                      ) : (
                        <img 
                          src={photo.preview} 
                          alt={`Preview ${index + 1}`}
                          className="w-full h-20 object-cover rounded-md"
                        />
                      )}
                      <button
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                completionPhotos.forEach(p => URL.revokeObjectURL(p.preview));
                setCompletionPhotos([]);
                setCompletionDialogOpen(false);
                setInstallationToComplete(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleCompleteWithPhotos}
              disabled={uploadingPhotos}
            >
              {uploadingPhotos 
                ? 'Bezig met uploaden...' 
                : installationToComplete?.photosOnly 
                  ? `Foto's Opslaan${completionPhotos.length > 0 ? ` (${completionPhotos.length})` : ''}`
                  : `Voltooien${completionPhotos.length > 0 ? ` met ${completionPhotos.length} foto's` : ''}`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel/Annuleer Dialog for Install/Clean users */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Installatie annuleren</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze installatie wilt annuleren? Geef een reden op.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason">Reden voor annulering *</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Bijv. Klant niet thuis, slecht weer, materiaal niet beschikbaar..."
              className="mt-2"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setCancelDialogOpen(false);
              setInstallationToCancel(null);
              setCancelReason('');
            }}>
              Terug
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelInstallation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!cancelReason.trim()}
            >
              Annuleren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Note/Opmerking Dialog for Install/Clean users */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Opmerking toevoegen</DialogTitle>
            <DialogDescription>
              Voeg een opmerking toe aan deze installatie.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="note">Opmerking</Label>
            <Textarea
              id="note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Bijv. Klant heeft speciale wensen, let op de hond, parkeren aan de achterkant..."
              className="mt-2"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setNoteDialogOpen(false);
              setInstallationForNote(null);
              setNewNote('');
            }}>
              Annuleren
            </Button>
            <Button onClick={handleSaveNote}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Popup viewer voor foto's & documenten */}
      <DocumentLightbox
        open={!!lightboxDoc}
        onOpenChange={(o) => { if (!o) setLightboxDoc(null); }}
        document={lightboxDoc}
      />
      
      {/* Legplannen & foto's beheren bij een in te plannen project */}
      <OfferteDocumenten
        open={!!docsDialogProject}
        onOpenChange={(o) => { if (!o) setDocsDialogProject(null); }}
        offerte={docsDialogProject ? { id: docsDialogProject.offerte_id, offerte_number: docsDialogProject.offerte_number } : null}
        entityType="installation_offerte"
        onChanged={() => refreshProjectDocs(docsDialogProject?.offerte_id)}
      />

      {/* Project opmerkingen dialoog */}
      <Dialog open={!!remarkProject} onOpenChange={(o) => { if (!o) setRemarkProject(null); }}>
        <DialogContent className="max-w-md" data-testid="project-remarks-dialog">
          <DialogHeader>
            <DialogTitle>Opmerkingen — {remarkProject?.customer_name}</DialogTitle>
            <DialogDescription>Zichtbaar voor de toegewezen install/clean accounts zodra de installatie ingepland is</DialogDescription>
          </DialogHeader>
          <Textarea value={remarkText} onChange={(e) => setRemarkText(e.target.value)} rows={4}
            placeholder="bv. kraan nodig, sleutel bij buren, parkeerverbod aanvragen..." data-testid="project-remarks-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemarkProject(null)}>Annuleren</Button>
            <Button onClick={saveProjectRemarks} disabled={remarkSaving} data-testid="project-remarks-save">
              {remarkSaving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Installaties;
